import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import DOMPurify from 'dompurify';
import { uploadFile, getSignedUrl } from '@/services/uploads';
import { fetchModule } from '@/services/courses';
import ModuleEditorBase from './ModuleEditorBase';
import { useKeyboardShortcuts, createRichTextShortcuts } from '@/hooks/useKeyboardShortcuts';

// ─── Image extension (defined at module scope so the React component
//     doesn't allocate a new extension class on every render — that was
//     causing the editor to be torn down + rebuilt mid-save, and the new
//     instance started with the pre-image content from props, which then
//     auto-saved over the freshly-saved image).
const ImageWithUploadKey = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      uploadKey: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-upload-key'),
        renderHTML: (attrs: { uploadKey?: string | null }) => {
          if (!attrs.uploadKey) return {};
          return { 'data-upload-key': attrs.uploadKey };
        },
      },
    };
  },
});

// ─── Text slash command items ───────────────────────────────────────────────

interface SlashItem {
  label: string;
  description: string;
  icon: React.ReactNode;
  action: (editor: any) => void;
  // When true, the consumer should bypass `action` and instead trigger
  // the image-upload file picker. The slash-menu itself can't reach the
  // editor's <input type="file"> ref directly, so this flag lets the
  // host component handle it.
  triggerImageUpload?: boolean;
}

const SLASH_ITEMS: SlashItem[] = [
  { label: 'Heading 1', description: 'Large heading', icon: <span className="font-bold text-sm">H1</span>, action: (e) => e.chain().focus().toggleHeading({ level: 1 }).run() },
  { label: 'Heading 2', description: 'Medium heading', icon: <span className="font-bold text-sm">H2</span>, action: (e) => e.chain().focus().toggleHeading({ level: 2 }).run() },
  { label: 'Heading 3', description: 'Small heading', icon: <span className="font-bold text-sm">H3</span>, action: (e) => e.chain().focus().toggleHeading({ level: 3 }).run() },
  { label: 'Image', description: 'Upload an image', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/><path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>, action: () => {}, triggerImageUpload: true },
  { label: 'Bullet List', description: 'Unordered list', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>, action: (e) => e.chain().focus().toggleBulletList().run() },
  { label: 'Numbered List', description: 'Ordered list', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M10 6h11M10 12h11M10 18h11M3 5v2M3 11v2M3 17v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>, action: (e) => e.chain().focus().toggleOrderedList().run() },
  { label: 'Quote', description: 'Blockquote', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" stroke="currentColor" strokeWidth="1.5"/></svg>, action: (e) => e.chain().focus().toggleBlockquote().run() },
  { label: 'Code Block', description: 'Fenced code', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>, action: (e) => e.chain().focus().toggleCodeBlock().run() },
  { label: 'Divider', description: 'Horizontal rule', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 12h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>, action: (e) => e.chain().focus().setHorizontalRule().run() },
];

interface TextSlashMenuProps {
  filter: string;
  onSelect: (item: SlashItem) => void;
  onClose: () => void;
}

function TextSlashMenu({ filter, onSelect, onClose }: TextSlashMenuProps) {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const filtered = SLASH_ITEMS.filter((item) =>
    item.label.toLowerCase().includes(filter.toLowerCase())
  );

  useEffect(() => { setFocusedIndex(0); }, [filter]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setFocusedIndex((i) => (i + 1) % Math.max(filtered.length, 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusedIndex((i) => (i - 1 + filtered.length) % Math.max(filtered.length, 1)); }
      else if (e.key === 'Enter') { e.preventDefault(); if (filtered[focusedIndex]) onSelect(filtered[focusedIndex]); }
      else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [filtered, focusedIndex, onSelect, onClose]);

  if (filtered.length === 0) return null;

  return (
    <div ref={menuRef} className="w-[220px] rounded-xl overflow-hidden shadow-lg border border-grey-500/20 bg-surface-primary py-1">
      {filtered.map((item, i) => (
        <button
          key={item.label}
          type="button"
          onClick={() => onSelect(item)}
          onMouseEnter={() => setFocusedIndex(i)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left cursor-pointer transition-colors duration-75 ${
            i === focusedIndex ? 'bg-primary-500/10 text-primary-600 dark:text-primary-350' : 'text-text-primary hover:bg-grey-500/10'
          }`}
        >
          <span className={`shrink-0 w-5 h-5 flex items-center justify-center ${i === focusedIndex ? 'text-primary-500' : 'text-text-secondary'}`}>
            {item.icon}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{item.label}</p>
            <p className="text-[11px] text-text-secondary truncate">{item.description}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── Main Editor ────────────────────────────────────────────────────────────

interface RichTextEditorProps {
  module: {
    id: string;
    title: string;
    type: string;
    details: { content?: string };
    embedded?: boolean;
    isFullscreen?: boolean;
    onToggleFullscreen?: () => void;
    registerCancelCallback?: (cb: () => Promise<void>) => void;
  };
  sectionId: string;
  courseId: string;
  onUpdate: (moduleId: string, updates: any) => Promise<void>;
  onDelete: (moduleId: string) => Promise<void>;
  /** Bubbles the live (sanitized) HTML on every edit so a host can render a
   *  live preview without waiting for the debounced auto-save. */
  onContentChange?: (html: string) => void;
}

export default function RichTextEditor({
  module,
  sectionId,
  courseId,
  onUpdate,
  onDelete,
  onContentChange,
}: RichTextEditorProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [htmlContent, setHtmlContent] = useState(module.details.content || '');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [slashMenuRect, setSlashMenuRect] = useState<DOMRect | null>(null);
  const [slashFilter, setSlashFilter] = useState('');
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Snapshot of initial HTML when the editor first mounts — used for cancel rollback
  const initialHtmlRef = useRef<string>(module.details.content || '');
  // Track if we've captured the initial state for this module
  const capturedModuleIdRef = useRef<string | null>(null);
  if (module.id !== capturedModuleIdRef.current) {
    capturedModuleIdRef.current = module.id;
    initialHtmlRef.current = module.details.content || '';
  }

  const sanitizeHTML = useCallback((html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p',
        'h1',
        'h2',
        'h3',
        'strong',
        'em',
        'u',
        'ul',
        'ol',
        'li',
        'a',
        'br',
        'code',
        'pre',
        'blockquote',
        'img',
        'hr',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'style', 'class', 'data-upload-key'],
    });
  }, []);

  const handleSlashTriggered = useCallback((rect: DOMRect) => {
    setSlashMenuRect(rect);
    setSlashFilter('');
    setSlashMenuOpen(true);
  }, []);

  // Memoized so `useEditor` sees a stable `extensions` reference and
  // doesn't recreate the editor on every parent re-render. None of these
  // extension instances depend on React state, so `[]` deps are safe.
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // StarterKit 3.x bundles Link + Underline. Disable them here so
        // our explicit `Link`/`Underline` imports below don't produce the
        // "Duplicate extension names" warning.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...({ link: false, underline: false } as any),
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      // `inline: true` lets the image sit inside the current paragraph
      // instead of being a block-level node — important because we insert
      // at the cursor (often mid-paragraph after a slash command). With
      // block images ProseMirror would silently reject the insertion when
      // the cursor is inside a paragraph that the schema can't split.
      ImageWithUploadKey.configure({ inline: true, allowBase64: false }),
      Placeholder.configure({
        placeholder: 'Write something awesome... Type "/" for formatting options',
      }),
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: module.details.content || '',
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      setHtmlContent(html);
      onContentChange?.(sanitizeHTML(html));
    },
    editorProps: {
      handleTextInput: (view, from, _to, text) => {
        if (text === '/') {
          const coords = view.coordsAtPos(from);
          const rect = new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top);
          requestAnimationFrame(() => handleSlashTriggered(rect));
        }
        return false;
      },
    },
  });

  useKeyboardShortcuts(editor ? createRichTextShortcuts(editor) : [], !!editor);

  // Close fullscreen on Escape + lock body scroll
  useEffect(() => {
    if (!isFullscreen) return;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKey);
    };
  }, [isFullscreen]);

  // Auto-save with debounce
  useEffect(() => {
    const sanitized = sanitizeHTML(htmlContent);
    if (sanitized !== (module.details.content || '')) {
      const timeout = setTimeout(async () => {
        try {
          setIsSaving(true);
          await onUpdate(module.id, { details: { content: sanitized } });
          setIsSaved(true);
          setTimeout(() => setIsSaved(false), 2000);
        } catch {
          // Autosave failed — isSaved stays false so the next edit retries.
        } finally {
          setIsSaving(false);
        }
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [htmlContent, module.id, onUpdate, sanitizeHTML, module.details.content]);

  // Sync content from external updates
  useEffect(() => {
    if (editor && module.details.content && module.details.content !== editor.getHTML()) {
      editor.commands.setContent(module.details.content, { emitUpdate: false });
    }
  }, [editor, module.details.content]);

  // Expose rollback function for cancel — restore initial state
  const handleRollback = useCallback(async () => {
    if (!editor) return;
    const initial = initialHtmlRef.current;
    editor.commands.setContent(initial, { emitUpdate: false });
    setHtmlContent(initial);
    try {
      await onUpdate(module.id, { details: { content: initial } });
    } catch {
      /* error is surfaced by the caller's toast handling */
    }
  }, [editor, module.id, onUpdate]);

  // Register rollback with parent overlay (for cancel)
  useEffect(() => {
    module.registerCancelCallback?.(handleRollback);
  }, [handleRollback, module.registerCancelCallback]);

  // Image upload handler.
  //
  // Inserts an <img> with two source-of-truth attributes:
  //   - `src`            → a (short-lived) presigned URL so the editor can
  //                        actually render the image during this session.
  //   - `data-upload-key`→ the durable S3 key. The learner-side viewer
  //                        regenerates a fresh presigned URL from this key
  //                        at render time, so saved HTML never serves a
  //                        stale URL.
  const handleImageUpload = useCallback(
    async (file: File) => {
      console.log('[RichTextEditor] handleImageUpload:start', {
        name: file.name,
        type: file.type,
        size: file.size,
        moduleId: module.id,
        hasEditor: !!editor,
      });
      if (!editor) {
        console.warn('[RichTextEditor] handleImageUpload aborted: no editor');
        return;
      }
      const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowed.includes(file.type) || file.size > 5 * 1024 * 1024) {
        console.warn('[RichTextEditor] handleImageUpload aborted: file rejected', {
          allowedType: allowed.includes(file.type),
          underSizeLimit: file.size <= 5 * 1024 * 1024,
        });
        return;
      }

      try {
        console.log('[RichTextEditor] uploadFile → POST /uploads…');
        const result = await uploadFile({
          file,
          resource_type: 'module_richtext',
          resource_id: module.id,
        });
        console.log('[RichTextEditor] uploadFile resolved', result);

        const signed = await getSignedUrl({ key: result.key });
        console.log('[RichTextEditor] getSignedUrl resolved', signed);

        // Insert as raw HTML so tiptap parses the <img> through our custom
        // parseHTML (which reads `data-upload-key` off the DOM). Using just
        // <img> (not wrapped in <p>) lets ProseMirror place it inside the
        // current paragraph since the schema is configured `inline: true`.
        const escapeAttr = (s: string) =>
          s
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        const html = `<img src="${escapeAttr(signed.url)}" alt="${escapeAttr(file.name)}" data-upload-key="${escapeAttr(result.key)}" />`;
        console.log('[RichTextEditor] inserting html', html);

        // Defer one tick so the file-picker blur settles before we re-focus.
        await new Promise((r) => setTimeout(r, 0));

        const beforeHtml = editor.getHTML();
        const beforePos = editor.state.selection.from;
        editor.commands.focus();
        const inserted = editor.commands.insertContent(html);
        const afterHtml = editor.getHTML();
        console.log('[RichTextEditor] insertContent', {
          inserted,
          beforePos,
          beforeLength: beforeHtml.length,
          afterLength: afterHtml.length,
          delta: afterHtml.length - beforeHtml.length,
          containsImg: afterHtml.includes('<img'),
          containsKey: afterHtml.includes('data-upload-key'),
        });
        if (!inserted) {
          console.warn('[RichTextEditor] first insertContent returned false; retrying at end');
          editor.commands.focus('end');
          const retry = editor.commands.insertContent(html);
          console.log('[RichTextEditor] retry insertContent at end', {
            retry,
            afterRetryLength: editor.getHTML().length,
            containsImg: editor.getHTML().includes('<img'),
          });
        }

        // Eagerly flush a save so the image lands on the server even if the
        // user navigates away before the 1500 ms debounce fires.
        try {
          const rawHtml = editor.getHTML();
          const sanitized = sanitizeHTML(rawHtml);
          console.log('[RichTextEditor] eager save — payload built', {
            moduleId: module.id,
            rawLength: rawHtml.length,
            sanitizedLength: sanitized.length,
            rawContainsImg: rawHtml.includes('<img'),
            sanitizedContainsImg: sanitized.includes('<img'),
            sanitizedContainsKey: sanitized.includes('data-upload-key'),
            htmlContentStateLength: htmlContent.length,
            htmlContentStateContainsImg: htmlContent.includes('<img'),
            moduleDetailsContentLength: (module.details.content || '').length,
            moduleDetailsContentContainsImg: (module.details.content || '').includes('<img'),
          });
          // FULL dump of what we're about to PUT — long, but the only way
          // to be 100% sure we're not sending a pre-image string.
          console.log('[RichTextEditor] eager save — FULL sanitized payload:', sanitized);
          console.log('[RichTextEditor] eager save — FULL raw editor HTML:', rawHtml);

          await onUpdate(module.id, { details: { content: sanitized } });
          console.log('[RichTextEditor] eager save — onUpdate resolved');

          // After the parent's save returns, see whether the editor or
          // module.details.content was reset to something different.
          const editorAfter = editor.getHTML();
          console.log('[RichTextEditor] eager save — post-await state', {
            editorAfterLength: editorAfter.length,
            editorAfterContainsImg: editorAfter.includes('<img'),
            stillEqualToSanitized: editorAfter === sanitized,
            moduleDetailsContentLength: (module.details.content || '').length,
            moduleDetailsContentContainsImg: (module.details.content || '').includes('<img'),
          });

          // Direct refetch from the server, bypassing TanStack cache, so we
          // can see exactly what the backend persisted vs what we sent.
          try {
            console.log('[RichTextEditor] post-save refetch — GET module…', {
              courseId,
              sectionId,
              moduleId: module.id,
            });
            const fresh = await fetchModule(courseId, sectionId, module.id);
            const freshContent =
              (fresh as { details?: { content?: string } } | null | undefined)?.details
                ?.content ?? '';
            console.log('[RichTextEditor] post-save refetch — server returned', {
              contentLength: freshContent.length,
              contentContainsImg: freshContent.includes('<img'),
              contentContainsKey: freshContent.includes('data-upload-key'),
              matchesSentSanitized: freshContent === sanitized,
            });
            console.log('[RichTextEditor] post-save refetch — FULL server content:', freshContent);
            console.log('[RichTextEditor] post-save refetch — full module object:', fresh);
          } catch (err) {
            console.error('[RichTextEditor] post-save refetch failed', err);
          }
          setIsSaved(true);
          setTimeout(() => setIsSaved(false), 2000);
        } catch (err) {
          console.error('[RichTextEditor] eager save failed', err);
          // best-effort — the debounced auto-save will retry
        }
      } catch (err) {
        console.error('[RichTextEditor] handleImageUpload upload/sign error', err);
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            editor.chain().focus().setImage({ src: reader.result, alt: file.name }).run();
          }
        };
        reader.readAsDataURL(file);
      }
    },
    // Include `onUpdate`, `sanitizeHTML`, and `module` so the closure
    // always captures the freshest references — particularly the parent's
    // save handler. Otherwise we could be calling a stale `onUpdate` that
    // dispatches with stale state from when the editor first mounted.
    [editor, module, onUpdate, sanitizeHTML]
  );

  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log('[RichTextEditor] file input change', {
      hasFile: !!file,
      name: file?.name,
      size: file?.size,
    });
    if (file) handleImageUpload(file);
    e.target.value = '';
  };

  const handleTitleChange = async (title: string) => {
    try {
      await onUpdate(module.id, { title });
    } catch {
      /* error is surfaced by the caller's toast handling */
    }
  };
  const handleDelete = async () => {
    try {
      await onDelete(module.id);
    } catch {
      /* error is surfaced by the caller's toast handling */
    }
  };

  // Handle slash menu selection
  const handleSlashSelect = useCallback(
    (item: SlashItem) => {
      if (!editor) return;
      setSlashMenuOpen(false);
      // Delete the "/" trigger character (and any filter the user typed
      // after it, if we ever wire that up).
      const { from } = editor.state.selection;
      editor
        .chain()
        .focus()
        .deleteRange({ from: from - 1, to: from })
        .run();
      if (item.triggerImageUpload) {
        // Open the file picker; the resulting upload is inserted at the
        // current cursor position by handleImageUpload via insertContent.
        console.log('[RichTextEditor] slash → image: opening file picker', {
          hasInputRef: !!imageInputRef.current,
        });
        imageInputRef.current?.click();
        return;
      }
      // Execute the formatting action
      item.action(editor);
    },
    [editor]
  );

  const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);

  if (!editor) {
    return <div className="text-sm text-text-secondary text-center py-8">Loading editor...</div>;
  }

  const currentHeading = editor.isActive('heading', { level: 1 })
    ? 'H1'
    : editor.isActive('heading', { level: 2 })
      ? 'H2'
      : editor.isActive('heading', { level: 3 })
        ? 'H3'
        : 'P';

  const ToolbarBtn = ({
    onClick,
    active,
    children,
    title: t,
  }: {
    onClick: () => void;
    active?: boolean;
    children: React.ReactNode;
    title?: string;
  }) => (
    <button
      type="button"
      onClick={onClick}
      title={t}
      className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold transition-colors duration-100 ${
        active
          ? 'bg-primary-500/15 text-primary-600 dark:text-primary-350'
          : 'text-text-secondary hover:bg-grey-500/10 hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  );

  const ToolbarSep = () => <div className="w-px h-6 bg-grey-500/20 mx-0.5 shrink-0" />;

  const editorCard = isFullscreen ? (
    // Fullscreen: opaque overlay with centered card
    <div className="fixed inset-0 z-[9999] bg-surface-primary">
      {/* Gradient bg */}
      <div
        className="brand-gradient absolute inset-0 pointer-events-none opacity-40"
        aria-hidden="true"
      />
      <div className="relative max-w-[960px] mx-auto h-full flex flex-col py-4 px-6">
        {/* Header */}
        <div className="flex items-center justify-between py-3 shrink-0">
          <h4 className="text-lg font-bold text-text-primary">Compose the Text</h4>
          <div className="flex items-center gap-1">
            <button type="button" onClick={toggleFullscreen} className="p-2 rounded-full text-text-primary hover:bg-grey-500/10 transition-colors duration-150" title="Exit fullscreen">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <button type="button" onClick={toggleFullscreen} className="p-2 rounded-full text-text-primary hover:bg-grey-500/10 transition-colors duration-150" title="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-3 py-2.5 rounded-2xl bg-surface-elevated/60 backdrop-blur-sm shadow-[-5px_5px_7px_rgba(0,0,0,0.1)] flex items-center gap-1 flex-wrap shrink-0 mb-3">
          {/* Heading dropdown */}
          <div className="relative">
            <select value={currentHeading} onChange={(e) => { const v = e.target.value; if (v === 'P') editor.chain().focus().setParagraph().run(); else if (v === 'H1') editor.chain().focus().toggleHeading({ level: 1 }).run(); else if (v === 'H2') editor.chain().focus().toggleHeading({ level: 2 }).run(); else if (v === 'H3') editor.chain().focus().toggleHeading({ level: 3 }).run(); }} className="h-9 pl-3 pr-7 rounded-xl bg-surface-elevated/80 border-[0.5px] border-white dark:border-grey-700 shadow-[-2px_1px_2px_rgba(0,0,0,0.08)] text-sm font-semibold text-text-primary appearance-none cursor-pointer outline-none">
              <option value="P">Paragraph</option><option value="H1">Heading 1</option><option value="H2">Heading 2</option><option value="H3">Heading 3</option>
            </select>
            <svg className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-text-secondary" width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <ToolbarSep />
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><span className="font-bold text-sm">B</span></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><span className="line-through text-sm">S</span></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><span className="italic text-sm">I</span></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline"><span className="underline text-sm">U</span></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M10 6h11M10 12h11M10 18h11M3 5v2M3 11v2M3 17v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></ToolbarBtn>
          <ToolbarSep />
          <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17 10H3M21 6H3M21 14H3M17 18H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 10H6M21 6H3M21 14H3M18 18H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 10H7M21 6H3M21 14H3M21 18H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 10H3M21 6H3M21 14H3M21 18H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></ToolbarBtn>
          <ToolbarSep />
          <ToolbarBtn onClick={() => { const url = window.prompt('Enter URL:'); if (url) editor.chain().focus().setLink({ href: url }).run(); }} active={editor.isActive('link')} title="Add link"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().unsetLink().run()} title="Remove link"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17 7l-10 10M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></ToolbarBtn>
          <ToolbarBtn onClick={() => imageInputRef.current?.click()} title="Insert image"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/><path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" stroke="currentColor" strokeWidth="1.5"/></svg></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Code"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></ToolbarBtn>
        </div>

        {/* Editor — only this scrolls */}
        <div ref={editorWrapperRef} className="flex-1 min-h-0 overflow-y-auto mb-3">
          <div className="max-w-none [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2 [&_hr]:border-grey-500/30 h-full">
            <EditorContent editor={editor} className={`p-4 bg-surface-elevated rounded-[20px] focus-within:ring-0 outline-none h-full
              [&_.ProseMirror]:outline-none [&_.ProseMirror]:h-full
              [&_.ProseMirror_h1]:text-[32px] [&_.ProseMirror_h1]:leading-[40px] [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mt-6 [&_.ProseMirror_h1]:mb-3 [&_.ProseMirror_h1]:text-text-primary
              [&_.ProseMirror_h2]:text-[24px] [&_.ProseMirror_h2]:leading-[32px] [&_.ProseMirror_h2]:font-bold [&_.ProseMirror_h2]:mt-5 [&_.ProseMirror_h2]:mb-2 [&_.ProseMirror_h2]:text-text-primary
              [&_.ProseMirror_h3]:text-[20px] [&_.ProseMirror_h3]:leading-[28px] [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mt-4 [&_.ProseMirror_h3]:mb-2 [&_.ProseMirror_h3]:text-text-primary
              [&_.ProseMirror_p]:text-[15px] [&_.ProseMirror_p]:leading-[24px] [&_.ProseMirror_p]:text-text-primary [&_.ProseMirror_p]:mb-1
              [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:mb-2
              [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:mb-2
              [&_.ProseMirror_li]:text-[15px] [&_.ProseMirror_li]:leading-[24px] [&_.ProseMirror_li]:mb-0.5
              [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-primary-500/40 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:italic [&_.ProseMirror_blockquote]:text-text-secondary [&_.ProseMirror_blockquote]:my-3
              [&_.ProseMirror_pre]:bg-grey-100 dark:[&_.ProseMirror_pre]:bg-grey-800 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:p-3 [&_.ProseMirror_pre]:text-sm [&_.ProseMirror_pre]:my-2 [&_.ProseMirror_pre]:overflow-x-auto
              [&_.ProseMirror_code]:bg-grey-100 dark:[&_.ProseMirror_code]:bg-grey-700 [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:text-sm [&_.ProseMirror_code]:text-primary-600 dark:[&_.ProseMirror_code]:text-primary-350
              [&_.ProseMirror_a]:text-primary-500 [&_.ProseMirror_a]:underline [&_.ProseMirror_a]:cursor-pointer
              [&_.ProseMirror_hr]:border-grey-500/30 [&_.ProseMirror_hr]:my-4
              [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-2
              [&_.ProseMirror_.is-empty]:before:text-text-disabled [&_.ProseMirror_.is-empty]:before:float-left [&_.ProseMirror_.is-empty]:before:pointer-events-none
            `} />
          </div>
          {slashMenuOpen && slashMenuRect && createPortal(
            <div className="fixed z-[10000]" style={{ top: slashMenuRect.bottom + 4, left: slashMenuRect.left }}>
              <TextSlashMenu filter={slashFilter} onSelect={handleSlashSelect} onClose={() => setSlashMenuOpen(false)} />
            </div>, document.body
          )}
        </div>

        {/* Bottom bar */}
        <div className="flex items-center justify-between py-3 shrink-0">
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => imageInputRef.current?.click()} className="p-2 rounded-full text-text-secondary hover:bg-grey-500/10 transition-colors" title="Add image">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/><path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <button type="button" className="p-2 rounded-full text-text-secondary hover:bg-grey-500/10 transition-colors" title="Attach file">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
          <div className="text-xs">
            {isSaving && <span className="text-text-secondary">Saving...</span>}
            {isSaved && <span className="text-success-600 dark:text-success-400">Saved</span>}
          </div>
        </div>

        <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleImageInputChange} />
      </div>
    </div>
  ) : (
    // Normal inline mode — flex column with a self-scrolling editor body so
    // the toolbar stays pinned at the top of the card regardless of the
    // outer modal/page scroll. `max-h-[80vh]` keeps the card inside the
    // surrounding overlay; `min-h-[400px]` guarantees a comfortable typing
    // surface when content is short.
    <div className="rounded-[20px] overflow-hidden relative flex flex-col min-h-[400px] max-h-[80vh]">
      <div
        className="brand-gradient absolute inset-0 pointer-events-none rounded-[20px] opacity-40"
        aria-hidden="true"
      />
      <div className="relative flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 shrink-0">
            <h4 className="text-lg font-bold text-text-primary">Compose the Text</h4>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleFullscreen}
                className="p-2 rounded-full text-text-primary hover:bg-grey-500/10 transition-colors duration-150"
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
              {isFullscreen && (
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="p-2 rounded-full text-text-primary hover:bg-grey-500/10 transition-colors duration-150"
                  title="Close"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Toolbar */}
          <div className="mx-3 mb-3 px-3 py-2.5 rounded-2xl bg-surface-elevated/85 backdrop-blur shadow-[-5px_5px_7px_rgba(0,0,0,0.1)] flex items-center gap-1 flex-wrap shrink-0">
            {/* Heading dropdown */}
            <div className="relative">
              <select
                value={currentHeading}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'P') editor.chain().focus().setParagraph().run();
                  else if (v === 'H1') editor.chain().focus().toggleHeading({ level: 1 }).run();
                  else if (v === 'H2') editor.chain().focus().toggleHeading({ level: 2 }).run();
                  else if (v === 'H3') editor.chain().focus().toggleHeading({ level: 3 }).run();
                }}
                className="h-9 pl-3 pr-7 rounded-xl bg-surface-elevated/80 border-[0.5px] border-white dark:border-grey-700 shadow-[-2px_1px_2px_rgba(0,0,0,0.08)] text-sm font-semibold text-text-primary appearance-none cursor-pointer outline-none"
              >
                <option value="P">Paragraph</option>
                <option value="H1">Heading 1</option>
                <option value="H2">Heading 2</option>
                <option value="H3">Heading 3</option>
              </select>
              <svg
                className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-text-secondary"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <ToolbarSep />

            {/* B S I */}
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleBold().run()}
              active={editor.isActive('bold')}
              title="Bold"
            >
              <span className="font-bold text-sm">B</span>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleStrike().run()}
              active={editor.isActive('strike')}
              title="Strikethrough"
            >
              <span className="line-through text-sm">S</span>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleItalic().run()}
              active={editor.isActive('italic')}
              title="Italic"
            >
              <span className="italic text-sm">I</span>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              active={editor.isActive('underline')}
              title="Underline"
            >
              <span className="underline text-sm">U</span>
            </ToolbarBtn>

            {/* Lists */}
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              active={editor.isActive('bulletList')}
              title="Bullet list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              active={editor.isActive('orderedList')}
              title="Numbered list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M10 6h11M10 12h11M10 18h11M3 5v2M3 11v2M3 17v2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ToolbarBtn>

            <ToolbarSep />

            {/* Text alignment */}
            <ToolbarBtn
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              active={editor.isActive({ textAlign: 'left' })}
              title="Align left"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M17 10H3M21 6H3M21 14H3M17 18H3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              active={editor.isActive({ textAlign: 'center' })}
              title="Align center"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 10H6M21 6H3M21 14H3M18 18H6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              active={editor.isActive({ textAlign: 'right' })}
              title="Align right"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21 10H7M21 6H3M21 14H3M21 18H7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().setTextAlign('justify').run()}
              active={editor.isActive({ textAlign: 'justify' })}
              title="Justify"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21 10H3M21 6H3M21 14H3M21 18H3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ToolbarBtn>

            <ToolbarSep />

            {/* Link + Unlink */}
            <ToolbarBtn
              onClick={() => {
                const url = window.prompt('Enter URL:');
                if (url) editor.chain().focus().setLink({ href: url }).run();
              }}
              active={editor.isActive('link')}
              title="Add link"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().unsetLink().run()}
              title="Remove link"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M17 7l-10 10M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </ToolbarBtn>

            {/* Insert image */}
            <ToolbarBtn onClick={() => imageInputRef.current?.click()} title="Insert image">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect
                  x="3"
                  y="3"
                  width="18"
                  height="18"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                <path
                  d="M21 15l-5-5L5 21"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ToolbarBtn>

            {/* Blockquote */}
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              active={editor.isActive('blockquote')}
              title="Quote"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </ToolbarBtn>

            {/* Code */}
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleCode().run()}
              active={editor.isActive('code')}
              title="Code"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M16 18l6-6-6-6M8 6l-6 6 6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ToolbarBtn>
          </div>

          {/* Editor content — self-scrolling so the toolbar above stays
              fixed at the top of the card regardless of outer scroll. */}
          <div
            ref={editorWrapperRef}
            className="mx-3 mb-3 flex-1 min-h-0 overflow-y-auto"
          >
            <div className="max-w-none [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-2 [&_hr]:border-grey-500/30 min-h-full">
              <EditorContent
                editor={editor}
                className={`p-4 bg-surface-elevated rounded-[20px] focus-within:ring-0 outline-none min-h-full
                [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-full
                [&_.ProseMirror_h1]:text-[32px] [&_.ProseMirror_h1]:leading-[40px] [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mt-6 [&_.ProseMirror_h1]:mb-3 [&_.ProseMirror_h1]:text-text-primary
                [&_.ProseMirror_h2]:text-[24px] [&_.ProseMirror_h2]:leading-[32px] [&_.ProseMirror_h2]:font-bold [&_.ProseMirror_h2]:mt-5 [&_.ProseMirror_h2]:mb-2 [&_.ProseMirror_h2]:text-text-primary
                [&_.ProseMirror_h3]:text-[20px] [&_.ProseMirror_h3]:leading-[28px] [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mt-4 [&_.ProseMirror_h3]:mb-2 [&_.ProseMirror_h3]:text-text-primary
                [&_.ProseMirror_p]:text-[15px] [&_.ProseMirror_p]:leading-[24px] [&_.ProseMirror_p]:text-text-primary [&_.ProseMirror_p]:mb-1
                [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:mb-2
                [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:mb-2
                [&_.ProseMirror_li]:text-[15px] [&_.ProseMirror_li]:leading-[24px] [&_.ProseMirror_li]:mb-0.5
                [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-primary-500/40 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:italic [&_.ProseMirror_blockquote]:text-text-secondary [&_.ProseMirror_blockquote]:my-3
                [&_.ProseMirror_pre]:bg-grey-100 dark:[&_.ProseMirror_pre]:bg-grey-800 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:p-3 [&_.ProseMirror_pre]:text-sm [&_.ProseMirror_pre]:my-2 [&_.ProseMirror_pre]:overflow-x-auto
                [&_.ProseMirror_code]:bg-grey-100 dark:[&_.ProseMirror_code]:bg-grey-700 [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:text-sm [&_.ProseMirror_code]:text-primary-600 dark:[&_.ProseMirror_code]:text-primary-350
                [&_.ProseMirror_a]:text-primary-500 [&_.ProseMirror_a]:underline [&_.ProseMirror_a]:cursor-pointer
                [&_.ProseMirror_hr]:border-grey-500/30 [&_.ProseMirror_hr]:my-4
                [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-2
                [&_.ProseMirror_.is-empty]:before:text-text-disabled [&_.ProseMirror_.is-empty]:before:float-left [&_.ProseMirror_.is-empty]:before:pointer-events-none
              `}
              />
            </div>

            {/* Text slash command menu */}
            {slashMenuOpen &&
              slashMenuRect &&
              createPortal(
                <div
                  className="fixed z-[100]"
                  style={{ top: slashMenuRect.bottom + 4, left: slashMenuRect.left }}
                >
                  <TextSlashMenu
                    filter={slashFilter}
                    onSelect={handleSlashSelect}
                    onClose={() => setSlashMenuOpen(false)}
                  />
                </div>,
                document.body
              )}
          </div>

          {/* Bottom bar — image + attach shortcuts + save indicator */}
          <div className="flex items-center justify-between px-4 pb-3 shrink-0">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="p-2 rounded-full text-text-secondary hover:bg-grey-500/10 transition-colors"
                title="Add image"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3"
                    y="3"
                    width="18"
                    height="18"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                  <path
                    d="M21 15l-5-5L5 21"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="p-2 rounded-full text-text-secondary hover:bg-grey-500/10 transition-colors"
                title="Attach file"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Auto-save indicator */}
            <div className="text-xs">
              {isSaving && <span className="text-text-secondary">Saving...</span>}
              {isSaved && <span className="text-success-600 dark:text-success-400">Saved</span>}
            </div>
          </div>
        </div>

        {/* Hidden image input */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleImageInputChange}
        />
      </div>
  );

  // When fullscreen, render via portal so it escapes any parent overflow/z-index context
  const editorContent = isFullscreen
    ? createPortal(editorCard, document.body)
    : editorCard;

  if (module.embedded) {
    return <>{editorContent}</>;
  }

  return (
    <ModuleEditorBase
      moduleType={module.type}
      moduleTitle={module.title}
      onTitleChange={handleTitleChange}
      onDelete={handleDelete}
      isSaving={isSaving}
      isSaved={isSaved}
    >
      {editorContent}
    </ModuleEditorBase>
  );
}
